/**
 * Transcribe action for audio blocks (Phase 2 of the local-first audio
 * pipeline): bytes from OPFS (else the IPFS gateway via service worker) →
 * `transcribeBlob` from @wovin/tranz (browser-direct Mistral Voxtral, batch)
 * → annotation-schema applogs on the same thread as the block.
 *
 * Canonical format: packages/@wovin/tranz/docs/annotation-schema.md —
 * `annotation/*` entities per segment + one `transcription/*` metadata
 * entity, all linked to the audio block via `annotation/recording` /
 * `transcription/recording`.
 */

import type { ApplogForInsertOptionalAgent, EntityID } from '@wovin/core/applog'
import type { ThreadOnlyCurrentNoDeleted } from '@wovin/core/thread'
import { queryAndMap } from '@wovin/core'
import { EntityID_LENGTH, mintNewEntity } from '@wovin/core/applog'
import { transcribeBlob } from '@wovin/tranz'
import { Logger } from 'besonders-logger'
import { createSignal } from 'solid-js'
import { insertApplogs } from '../data/ApplogDB'
import { effectiveTranscribeModel, requireMistralApiKey } from './ai-settings'
import { readBlobFromOpfs } from './blob-upload'

const { LOG, WARN, DEBUG } = Logger.setup(Logger.DEBUG, { prefix: '[transcribe]' })

const [transcribingBlocks, setTranscribingBlocks] = createSignal<ReadonlySet<EntityID>>(new Set())
export { transcribingBlocks }
export const isTranscribing = (blockID: EntityID) => transcribingBlocks().has(blockID)

function markTranscribing(blockID: EntityID, active: boolean) {
	setTranscribingBlocks((prev) => {
		const next = new Set(prev)
		if (active) next.add(blockID)
		else next.delete(blockID)
		return next
	})
}

/** Resolve the audio bytes for a block: OPFS (recorded here) else gateway. */
export async function getAudioBlobForBlock(cidStr: string, mimeType?: string): Promise<Blob> {
	const local = await readBlobFromOpfs(cidStr)
	if (local) return local
	const res = await fetch(`/ipfs/${cidStr}`)
	if (!res.ok) throw new Error(`Failed to fetch audio bytes for ${cidStr}: ${res.status}`)
	const blob = await res.blob()
	// Gateway responses may lack a usable content-type — reapply the block's.
	return mimeType && !blob.type ? new Blob([blob], { type: mimeType }) : blob
}

/**
 * Transcribe an audio block and write annotation-schema applogs.
 * Returns the transcription (metadata) entity ID, or null when skipped.
 */
export async function transcribeAudioBlock(blockID: EntityID, thread: ThreadOnlyCurrentNoDeleted): Promise<EntityID | null> {
	if (isTranscribing(blockID)) {
		WARN('Transcription already running for block', blockID)
		return null
	}
	const apiKey = requireMistralApiKey()
	const cid = queryAndMap(thread, [{ en: blockID, at: 'blob/cid', vl: '?cid' }], 'cid')[0] as string | undefined
	if (!cid) throw new Error('Block has no blob/cid — nothing to transcribe')
	const mimeType = queryAndMap(thread, [{ en: blockID, at: 'blob/mimeType', vl: '?mime' }], 'mime')[0] as string | undefined
	const durationMs = queryAndMap(thread, [{ en: blockID, at: 'media/duration', vl: '?dur' }], 'dur')[0] as number | undefined

	markTranscribing(blockID, true)
	try {
		const audioBlob = await getAudioBlobForBlock(cid, mimeType)
		const model = effectiveTranscribeModel()
		LOG('Transcribing block', blockID, { cid, mimeType, size: audioBlob.size, model })

		const result = await transcribeBlob(audioBlob, { apiKey, model, mimeType })
		if (result.error) throw new Error(result.error)
		DEBUG('Transcription result', result)

		const ts = new Date().toISOString()
		const trxId = mintNewEntity({ recording: blockID, provider: 'mistral', model, ts }, EntityID_LENGTH)
		const applogs: ApplogForInsertOptionalAgent[] = [
			{ en: trxId, at: 'transcription/provider', vl: 'mistral' },
			{ en: trxId, at: 'transcription/model', vl: result.model ?? model },
			{ en: trxId, at: 'transcription/recording', vl: blockID },
		]
		if (result.language) applogs.push({ en: trxId, at: 'transcription/primaryLanguage', vl: result.language })

		// One annotation entity per segment; a segment-less result (e.g. when a
		// language is forced, disabling timestamps) becomes a single full-length
		// annotation so downstream consumers always find annotation/* data.
		const segments = result.segments?.length
			? result.segments
			: [{ startMs: 0, endMs: durationMs ?? 0, text: result.text, diarization: undefined }]
		for (const seg of segments) {
			if (!seg.text?.trim()) continue
			const annId = mintNewEntity({ recording: blockID, startMs: seg.startMs, endMs: seg.endMs, transcript: seg.text, ts }, EntityID_LENGTH)
			applogs.push(
				{ en: annId, at: 'annotation/startMs', vl: Math.round(seg.startMs) },
				{ en: annId, at: 'annotation/endMs', vl: Math.round(seg.endMs) },
				{ en: annId, at: 'annotation/transcript', vl: seg.text },
				{ en: annId, at: 'annotation/recording', vl: blockID },
				{ en: annId, at: 'annotation/sourceTranscript', vl: trxId },
			)
			if (seg.diarization != null) applogs.push({ en: annId, at: 'annotation/diarization', vl: seg.diarization })
		}

		insertApplogs(thread, applogs)
		LOG('Transcribed block', blockID, '→ transcription entity', trxId, `(${segments.length} segments)`)
		return trxId
	} finally {
		markTranscribing(blockID, false)
	}
}
